import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Tabs,
  Form,
  InputNumber,
  Select,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { StrategyContextStrip } from '@/components/owner-intelligence/StrategyContextStrip';
import { strategyService } from '@/services/strategy.service';

const { Title, Paragraph, Text } = Typography;

const EnterpriseStrategyPage: React.FC = () => {
  const qc = useQueryClient();
  const [question, setQuestion] = useState('Are we achieving our five-year strategy?');
  const [qa, setQa] = useState<Record<string, unknown> | null>(null);

  const dashQ = useQuery({
    queryKey: ['esbi-dashboard'],
    queryFn: () => strategyService.getDashboard(),
    staleTime: 30_000,
  });
  const objsQ = useQuery({
    queryKey: ['esbi-objectives'],
    queryFn: () => strategyService.listObjectives(),
    staleTime: 30_000,
  });
  const scoreQ = useQuery({
    queryKey: ['esbi-scorecard'],
    queryFn: () => strategyService.getScorecard(),
    staleTime: 60_000,
  });
  const kpiQ = useQuery({
    queryKey: ['esbi-kpis'],
    queryFn: () => strategyService.listKpis(),
    staleTime: 60_000,
  });
  const pfQ = useQuery({
    queryKey: ['esbi-portfolio'],
    queryFn: () => strategyService.getPortfolio(),
    staleTime: 60_000,
  });
  const invQ = useQuery({
    queryKey: ['esbi-investments'],
    queryFn: () => strategyService.listInvestments(),
    staleTime: 60_000,
  });
  const riskQ = useQuery({
    queryKey: ['esbi-risks'],
    queryFn: () => strategyService.listRisks(),
    staleTime: 60_000,
  });
  const heatQ = useQuery({
    queryKey: ['esbi-heatmap'],
    queryFn: () => strategyService.getHeatmap(),
    staleTime: 60_000,
  });
  const boardQ = useQuery({
    queryKey: ['esbi-board'],
    queryFn: () => strategyService.getBoardWorkspace(),
    staleTime: 60_000,
  });
  const simQ = useQuery({
    queryKey: ['esbi-sims'],
    queryFn: () => strategyService.listSimulations(),
    staleTime: 60_000,
  });

  const createObj = useMutation({
    mutationFn: (v: any) => strategyService.createObjective(v),
    onSuccess: () => {
      message.success('Objective created');
      void qc.invalidateQueries({ queryKey: ['esbi-objectives'] });
      void qc.invalidateQueries({ queryKey: ['esbi-dashboard'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const createKr = useMutation({
    mutationFn: (v: any) => strategyService.createKeyResult(v),
    onSuccess: () => message.success('Key result added'),
    onError: (e: Error) => message.error(e.message),
  });
  const createInv = useMutation({
    mutationFn: (v: any) => strategyService.createInvestment(v),
    onSuccess: () => {
      message.success('Investment case drafted (advisory)');
      void qc.invalidateQueries({ queryKey: ['esbi-investments'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const runSim = useMutation({
    mutationFn: (v: any) => strategyService.runSimulation({ ...v, confirm: true }),
    onSuccess: () => {
      message.success('Simulation completed (advisory)');
      void qc.invalidateQueries({ queryKey: ['esbi-sims'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const genPack = useMutation({
    mutationFn: () => strategyService.getBoardPack(true),
    onSuccess: () => {
      message.success('Board pack generated');
      void qc.invalidateQueries({ queryKey: ['esbi-board'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const askMut = useMutation({
    mutationFn: () => strategyService.getBrief('ceo', question),
    onSuccess: (d) => setQa(d),
    onError: (e: Error) => message.error(e.message),
  });
  const meetMut = useMutation({
    mutationFn: (v: any) => strategyService.createMeeting(v),
    onSuccess: () => {
      message.success('Meeting recorded');
      void qc.invalidateQueries({ queryKey: ['esbi-board'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const decisionMut = useMutation({
    mutationFn: (v: any) => strategyService.recordDecision(v),
    onSuccess: () => {
      message.success('Decision registered (human accountability retained)');
      void qc.invalidateQueries({ queryKey: ['esbi-board'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const widgets = ((dashQ.data as any)?.widgets || {}) as Record<string, any>;
  const objectives = (objsQ.data as any[]) || [];
  const risks = (riskQ.data as any[]) || [];
  const investments = (invQ.data as any[]) || [];
  const heatGrid = (((heatQ.data as any)?.grid as any[]) || []).filter((g) => g.count > 0);
  const board = (boardQ.data as any) || {};

  return (
    <OiPageFrame
      subtitle="Enterprise Strategy & Board Intelligence — advisory only. Never authorizes investments or ERP mutations."
      loading={dashQ.isLoading && !dashQ.data}
      error={Boolean(dashQ.error)}
      errorMessage="Strategy dashboard unavailable"
    >
      <StrategyContextStrip />
      <Space style={{ marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          Enterprise Strategy
        </Title>
        <Link to="/owner-intelligence/strategy">Classic strategy alignment</Link>
        <Link to="/owner-intelligence/board-packs">OI Board packs</Link>
        <Link to="/owner-intelligence/scenarios">Scenarios</Link>
      </Space>

      {(dashQ.data as any)?.degraded ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          ESBI degraded (`ESBI_JOBS_DISABLED=1`).
        </Card>
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Objectives" value={widgets.strategicObjectives?.objectivesActive ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="KR completion %" value={widgets.strategicObjectives?.avgCompletionPct ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Investments" value={widgets.investmentPipeline ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Pulse" value={widgets.enterprisePulse ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Capacity" value={widgets.capacityUtilization ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Meetings" value={widgets.boardMeetings ?? '—'} />
        </Col>
      </Row>

      <Tabs
        items={[
          {
            key: 'objectives',
            label: 'Objectives & OKRs',
            children: (
              <>
                <Card title="Create objective" style={{ marginBottom: 16 }}>
                  <Form
                    layout="inline"
                    onFinish={(v) => createObj.mutate(v)}
                    initialValues={{ levelKind: 'objective', priority: 'high' }}
                  >
                    <Form.Item name="title" rules={[{ required: true }]}>
                      <Input placeholder="Title" style={{ width: 280 }} />
                    </Form.Item>
                    <Form.Item name="levelKind">
                      <Select
                        style={{ width: 140 }}
                        options={['vision', 'theme', 'objective', 'business_unit', 'department'].map(
                          (v) => ({ value: v, label: v })
                        )}
                      />
                    </Form.Item>
                    <Form.Item name="horizonYears">
                      <InputNumber placeholder="Horizon yrs" min={1} max={10} />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={createObj.isPending}>
                      Add
                    </Button>
                  </Form>
                </Card>
                <Card title="Add key result" style={{ marginBottom: 16 }}>
                  <Form layout="inline" onFinish={(v) => createKr.mutate(v)}>
                    <Form.Item name="objectiveId" rules={[{ required: true }]}>
                      <Select
                        style={{ width: 260 }}
                        placeholder="Objective"
                        options={objectives.map((o) => ({ value: o.id, label: o.title }))}
                      />
                    </Form.Item>
                    <Form.Item name="title" rules={[{ required: true }]}>
                      <Input placeholder="KR title" style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item name="targetValue">
                      <InputNumber placeholder="Target" />
                    </Form.Item>
                    <Form.Item name="currentValue">
                      <InputNumber placeholder="Current" />
                    </Form.Item>
                    <Button htmlType="submit" loading={createKr.isPending}>
                      Add KR
                    </Button>
                  </Form>
                </Card>
                <Table
                  rowKey={(r) => String(r.id)}
                  dataSource={objectives}
                  size="small"
                  columns={[
                    { title: 'Level', dataIndex: 'level_kind', render: (v) => <Tag>{v}</Tag> },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Owner', dataIndex: 'owner_name' },
                    { title: 'Status', dataIndex: 'status' },
                    { title: 'Horizon', dataIndex: 'horizon_years' },
                  ]}
                  locale={{
                    emptyText: (
                      <OiEmptyState title="No objectives" body="Create vision / themes / objectives above." />
                    ),
                  }}
                />
              </>
            ),
          },
          {
            key: 'scorecard',
            label: 'Balanced Scorecard',
            children: (
              <>
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  {Object.entries(((scoreQ.data as any)?.perspectiveScores as any) || {}).map(
                    ([k, v]: any) => (
                      <Col xs={12} md={8} lg={4} key={k}>
                        <PrimaryKpiCard label={k} value={`${v.avgPct ?? 0}% (${v.count})`} />
                      </Col>
                    )
                  )}
                </Row>
                <Table
                  rowKey={(r) => String(r.id)}
                  dataSource={(kpiQ.data as any[]) || []}
                  size="small"
                  columns={[
                    { title: 'Code', dataIndex: 'code' },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Perspective', dataIndex: 'perspective' },
                    { title: 'Current', dataIndex: 'current_value' },
                    { title: 'Target', dataIndex: 'target_value' },
                    { title: 'Trend', dataIndex: 'trend' },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'portfolio',
            label: 'Portfolio & Investments',
            children: (
              <>
                <Paragraph>
                  Portfolio items: {((pfQ.data as any)?.items || []).length}. Advisory investment math only.
                </Paragraph>
                <Card title="Draft investment case" style={{ marginBottom: 16 }}>
                  <Form
                    layout="inline"
                    onFinish={(v) => createInv.mutate(v)}
                    initialValues={{ amount: 1000000, investmentKind: 'technology' }}
                  >
                    <Form.Item name="title" rules={[{ required: true }]}>
                      <Input placeholder="Title" style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item name="code" rules={[{ required: true }]}>
                      <Input placeholder="Code" style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name="amount">
                      <InputNumber min={0} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name="investmentKind">
                      <Select
                        style={{ width: 160 }}
                        options={[
                          'factory_expansion',
                          'warehouse',
                          'acquisition',
                          'technology',
                          'automation',
                          'recruitment',
                        ].map((v) => ({ value: v, label: v }))}
                      />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={createInv.isPending}>
                      Model (advisory)
                    </Button>
                  </Form>
                </Card>
                <Table
                  rowKey={(r) => String(r.id)}
                  dataSource={investments}
                  size="small"
                  columns={[
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Amount', dataIndex: 'amount' },
                    { title: 'NPV', dataIndex: 'npv' },
                    { title: 'IRR %', dataIndex: 'irr_pct' },
                    { title: 'ROI %', dataIndex: 'roi_pct' },
                    { title: 'Payback mo', dataIndex: 'payback_months' },
                    { title: 'Status', dataIndex: 'status' },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'risks',
            label: 'Strategic Risk',
            children: (
              <>
                <Card title="Heatmap (non-zero cells)" style={{ marginBottom: 16 }}>
                  <Table
                    size="small"
                    rowKey={(r) => `${r.likelihood}-${r.impact}`}
                    dataSource={heatGrid}
                    pagination={false}
                    columns={[
                      { title: 'Likelihood', dataIndex: 'likelihood' },
                      { title: 'Impact', dataIndex: 'impact' },
                      { title: 'Count', dataIndex: 'count' },
                      {
                        title: 'Titles',
                        dataIndex: 'titles',
                        render: (t: string[]) => (t || []).join(', '),
                      },
                    ]}
                  />
                </Card>
                <Table
                  rowKey={(r) => String(r.id)}
                  dataSource={risks}
                  size="small"
                  columns={[
                    { title: 'Category', dataIndex: 'risk_category' },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'L', dataIndex: 'likelihood', width: 50 },
                    { title: 'I', dataIndex: 'impact', width: 50 },
                    { title: 'Exposure', dataIndex: 'exposure' },
                    { title: 'Status', dataIndex: 'status' },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'board',
            label: 'Board Workspace',
            children: (
              <>
                <Space style={{ marginBottom: 12 }}>
                  <Button type="primary" loading={genPack.isPending} onClick={() => genPack.mutate()}>
                    Generate Board Pack
                  </Button>
                </Space>
                <Card title="New meeting" style={{ marginBottom: 16 }}>
                  <Form layout="inline" onFinish={(v) => meetMut.mutate(v)}>
                    <Form.Item name="title" rules={[{ required: true }]}>
                      <Input placeholder="Meeting title" style={{ width: 280 }} />
                    </Form.Item>
                    <Button htmlType="submit" loading={meetMut.isPending}>
                      Add
                    </Button>
                  </Form>
                </Card>
                <Card title="Record decision" style={{ marginBottom: 16 }}>
                  <Form layout="inline" onFinish={(v) => decisionMut.mutate(v)}>
                    <Form.Item name="title" rules={[{ required: true }]}>
                      <Input placeholder="Decision title" style={{ width: 240 }} />
                    </Form.Item>
                    <Form.Item name="decisionText">
                      <Input placeholder="Notes" style={{ width: 280 }} />
                    </Form.Item>
                    <Button htmlType="submit" loading={decisionMut.isPending}>
                      Register
                    </Button>
                  </Form>
                </Card>
                <Row gutter={16}>
                  <Col span={12}>
                    <Card title="Meetings" size="small">
                      <Table
                        size="small"
                        pagination={false}
                        rowKey={(r) => String(r.id)}
                        dataSource={(board.meetings as any[]) || []}
                        columns={[
                          { title: 'Title', dataIndex: 'title' },
                          { title: 'Status', dataIndex: 'status' },
                        ]}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="Decisions" size="small">
                      <Table
                        size="small"
                        pagination={false}
                        rowKey={(r) => String(r.id)}
                        dataSource={(board.decisions as any[]) || []}
                        columns={[
                          { title: 'Title', dataIndex: 'title' },
                          { title: 'Follow-up', dataIndex: 'follow_up' },
                        ]}
                      />
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },
          {
            key: 'sims',
            label: 'Simulations & Briefs',
            children: (
              <>
                <Card title="Long-horizon simulation" style={{ marginBottom: 16 }}>
                  <Form
                    layout="inline"
                    onFinish={(v) =>
                      runSim.mutate({
                        title: v.title,
                        scenarioKind: v.scenarioKind,
                        horizonYears: v.horizonYears,
                        inputs: { revenueGrowthPct: v.revenueGrowthPct, baseRevenue: v.baseRevenue },
                      })
                    }
                    initialValues={{
                      title: '5-year growth',
                      scenarioKind: 'market_expansion',
                      horizonYears: 5,
                      revenueGrowthPct: 8,
                      baseRevenue: 50000000,
                    }}
                  >
                    <Form.Item name="title" rules={[{ required: true }]}>
                      <Input style={{ width: 180 }} />
                    </Form.Item>
                    <Form.Item name="scenarioKind">
                      <Select
                        style={{ width: 180 }}
                        options={[
                          'market_expansion',
                          'acquire_company',
                          'new_plant',
                          'ipo',
                          'commodity_inflation',
                          'supply_disruption',
                        ].map((v) => ({ value: v, label: v }))}
                      />
                    </Form.Item>
                    <Form.Item name="horizonYears">
                      <Select
                        style={{ width: 100 }}
                        options={[3, 5, 10].map((v) => ({ value: v, label: `${v}y` }))}
                      />
                    </Form.Item>
                    <Form.Item name="revenueGrowthPct">
                      <InputNumber />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={runSim.isPending}>
                      Run (confirm)
                    </Button>
                  </Form>
                  <Paragraph type="secondary" style={{ marginTop: 8 }}>
                    Requires confirm:true on API — does not authorize investments.
                  </Paragraph>
                </Card>
                <Table
                  size="small"
                  rowKey={(r) => String(r.id)}
                  dataSource={(simQ.data as any[]) || []}
                  columns={[
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Kind', dataIndex: 'scenario_kind' },
                    { title: 'Horizon', dataIndex: 'horizon_years' },
                    { title: 'Status', dataIndex: 'status' },
                    { title: 'Confidence', dataIndex: 'confidence' },
                  ]}
                />
                <Card title="Intelligent executive question" style={{ marginTop: 16 }}>
                  <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
                    <Input value={question} onChange={(e) => setQuestion(e.target.value)} />
                    <Button type="primary" loading={askMut.isPending} onClick={() => askMut.mutate()}>
                      Ask
                    </Button>
                  </Space.Compact>
                  {qa ? (
                    <>
                      <Paragraph>
                        <Text strong>{String((qa as any).answer || (qa as any).body_markdown || '')}</Text>
                      </Paragraph>
                      <Paragraph type="secondary">
                        Confidence {(qa as any).confidence ?? '—'} —{' '}
                        {((qa as any).citations || [])
                          .map((c: any) => c.source)
                          .join(', ')}
                      </Paragraph>
                    </>
                  ) : null}
                </Card>
              </>
            ),
          },
        ]}
      />
    </OiPageFrame>
  );
};

export default EnterpriseStrategyPage;
